import {MemoryModel} from "../models/MemoryModel";
import {SystemMemoryProps} from "../models/SystemMemoryProps";
import {Dispatch, SetStateAction} from "react";
import {Authentication} from "../logic/Authentication";
import {HTTPError} from "../models/errors/HTTPError";

type ExecuteItem<T> = T | undefined;

export class ApiDataHelper {
    private readonly GetMemory: MemoryModel;
    private readonly PutMemory: Dispatch<SetStateAction<MemoryModel>>;

    constructor({memory}: SystemMemoryProps) {
        this.GetMemory = memory[0];
        this.PutMemory = memory[1];
    }

    private async calculateSHA256Hash(inputString: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(inputString);
        const buffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(buffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    }

    private async GetCache<T>(Url: string, Init: string): Promise<T> {
        let hash = await this.calculateSHA256Hash(Url + Init);

        for (let i = 0; i < this.GetMemory.Cache.length; i++) {
            let data = this.GetMemory.Cache[i]
            if (data.ID === hash) {
                if (data.Expiration > new Date().getTime()) {
                    return JSON.parse(data.Content);
                }
                else {
                    this.GetMemory.Cache.splice(i, 1);
                    this.PutMemory(this.GetMemory)
                }
            }
        }

        throw new Error("Item not in local Cache")
    }

    private async PutCache(Url: string, Init: string, Response: string, Expiration: number) {
        let hash = await this.calculateSHA256Hash(Url + Init);

        for (let i = 0; i < this.GetMemory.Cache.length; i++) {
            let data = this.GetMemory.Cache[i]
            if (data.ID === hash) {
                this.GetMemory.Cache.splice(i, 1);
                this.PutMemory(this.GetMemory)
                break;
            }
        }

        this.GetMemory.Cache.push({
            ID: hash,
            Content: Response,
            Expiration: new Date().getTime() + (Expiration * 1000)
        })
        this.PutMemory(this.GetMemory)
    }

    public async PurgeCache(url: string = 'null', Init: string = 'null') {
        // If a specific URL is provided, only remove that URL from the cache
        if (url !== 'null' && Init !== 'null') {
            let hash = await this.calculateSHA256Hash((this.GetMemory.ApiURL + url) + Init);
            for (let i = 0; i < this.GetMemory.Cache.length; i++) {
                if (this.GetMemory.Cache[i].ID === hash) {
                    this.GetMemory.Cache.splice(i, 1);
                    this.PutMemory(this.GetMemory)
                    break;
                }
            }
        }
        // If no URL is provided, remove all items from the cache
        else {
            this.GetMemory.Cache = [];
            this.PutMemory(this.GetMemory)
        }
    }

    public async Execute<T>(ApiPath: string, init: any, useCache: boolean = true, setCacheExpiration: number = 5, hasRefreshed: boolean = false): Promise<ExecuteItem<T>> {
        if (useCache) {
            try {
                return await this.GetCache<T>(this.GetMemory.ApiURL + ApiPath, JSON.stringify(init));
            }
            catch(e) {
                console.log(e);
            }
        }

        init.headers['Authorization'] = `Bearer ${this.GetMemory.AccessToken}`;

        const data = await fetch(this.GetMemory.ApiURL + ApiPath, init)
        if (data.ok) {
            let response: T = await data.json();

            if (useCache) {
                // Remove the authorization header
                delete init.headers['Authorization'];

                await this.PutCache(this.GetMemory.ApiURL + ApiPath, JSON.stringify(init), JSON.stringify(response), setCacheExpiration)
            }

            return response;
        }
        else if (data.status === 401){
            const statusText = await data.text();
            // Check if the 401 code was called due to an invalid 2FA code
            if (statusText.includes("2FA") && ApiPath === "/auth/2fa/authenticate") {
                throw new HTTPError(406, "Invalid 2FA code.")
            }
            // Check if the issue is due to lack of 2FA
            else if (statusText.includes("2FA")) {
                this.GetMemory.URLBefore2FA = window.location.pathname;
                this.PutMemory(this.GetMemory);

                this.GetMemory.Router('/2fa');
            }
            else {
                // Refresh the JWT and try again
                if (this.GetMemory.AccessToken.length > 0 && !(hasRefreshed)) {
                    let auth = new Authentication({memory: [this.GetMemory, this.PutMemory]});
                    await auth.RefreshJwT();

                    return await this.Execute(ApiPath, init, useCache, setCacheExpiration, true)
                }
                else {
                    // If the JWT is not valid, redirect to the login page
                    this.GetMemory.AccessToken = '';
                    this.GetMemory.isSignedIn = false;
                    this.PutMemory(this.GetMemory);

                    this.GetMemory.Router('/login');
                }
            }
        }
        else {
            throw new HTTPError(data.status, (await data.text()).replaceAll('"', ""))
        }
    }

    public async ExecuteWithoutBody(ApiPath: string, init: any, hasRefreshed: boolean = false): Promise<void> {
        init.headers['Authorization'] = `Bearer ${this.GetMemory.AccessToken}`;

        const data = await fetch(this.GetMemory.ApiURL + ApiPath, init)
        if (data.ok) {
            return;
        }
        else if (data.status === 401){
            const statusText = await data.text();
            // Check if the 401 code was called due to an invalid 2FA code
            if (statusText.includes("2FA") && ApiPath === "/auth/2fa/authenticate") {
                throw new HTTPError(406, "Invalid 2FA code.")
            }
            // Check if the issue is due to lack of 2FA
            else if (statusText.includes("2FA")) {
                this.GetMemory.URLBefore2FA = window.location.pathname;
                this.PutMemory(this.GetMemory);

                this.GetMemory.Router('/2fa');
            }
            else {
                // Refresh the JWT and try again
                if (this.GetMemory.AccessToken.length > 0 && !(hasRefreshed)) {
                    let auth = new Authentication({memory: [this.GetMemory, this.PutMemory]});

                    try {
                        await auth.RefreshJwT();
                    }
                    catch (e) {
                        // Check for HttpError
                        if (e instanceof HTTPError) {
                            // If the user is not signed in, redirect to the login page
                            if (e.code === 401) {
                                this.GetMemory.Router('/login');
                            }
                            else {
                                // If the error has no method to handle it then throw it
                                throw e;
                            }
                        }
                        else {
                            // If the error is not an HTTPError then throw it
                            throw e;
                        }
                    }

                    return await this.ExecuteWithoutBody(ApiPath, init, true)
                }
                else {
                    // If the JWT is not valid, redirect to the login page
                    this.GetMemory.Router('/login');
                }
            }
        }
        else {
            throw new HTTPError(data.status, (await data.text()).replaceAll('"', ""))
        }
    }
}